import { useMemo, useRef, useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Button, Input, Paragraph, Separator, Spinner, Text, XStack, YStack } from 'tamagui';
import type { PaperlessCorrespondent, PaperlessDocument } from '@mrrmlab/api-client';
import { useDebouncedValue } from '../hooks/use-debounced-value';
import {
  useDocumentList,
  usePaperlessCorrespondents,
  usePaperlessDocumentTypes,
  usePaperlessTags,
  useUploadDocument,
} from '../archiv/hooks';

const PAGE_SIZE = 25;

export function ArchivRoute() {
  const navigate = useNavigate();
  const search = useSearch({ from: '/archiv' });
  const [query, setQuery] = useState(search.query ?? '');
  const debouncedQuery = useDebouncedValue(query, 300);

  const tagIds = useMemo(() => parseCsvInts(search.tags), [search.tags]);
  const correspondentId = search.correspondent;
  const documentTypeId = search.documentType;
  const page = search.page ?? 1;

  const { data, isLoading, error } = useDocumentList({
    query: debouncedQuery || undefined,
    tags: tagIds.length ? tagIds : undefined,
    correspondent: correspondentId,
    documentType: documentTypeId,
    page,
    pageSize: PAGE_SIZE,
    ordering: '-created',
  });

  const { data: tagsList } = usePaperlessTags();
  const { data: correspondentsList } = usePaperlessCorrespondents();
  const { data: documentTypesList } = usePaperlessDocumentTypes();
  const upload = useUploadDocument();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploadMessage, setUploadMessage] = useState<string | null>(null);

  function onFilePicked(e: { target: HTMLInputElement }) {
    const file = e.target.files?.[0];
    if (!file) return;
    const title = file.name.replace(/\.[^.]+$/, '');
    upload.mutate(
      { file, meta: { title } },
      {
        onSuccess: () => setUploadMessage(`„${title}“ eingereicht. Paperless verarbeitet das Dokument.`),
        onError: (err) =>
          setUploadMessage(
            `Upload fehlgeschlagen: ${err instanceof Error ? err.message : String(err)}`,
          ),
      },
    );
    e.target.value = '';
  }

  function setSearchPartial(partial: Partial<ArchivSearch>) {
    navigate({
      to: '/archiv',
      search: (prev) => ({ ...prev, ...partial, page: partial.page ?? 1 }),
    });
  }

  function toggleTag(id: number) {
    const next = tagIds.includes(id) ? tagIds.filter((t) => t !== id) : [...tagIds, id];
    setSearchPartial({ tags: next.length ? next.join(',') : undefined });
  }

  const total = data?.count ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <YStack padding="$4" gap="$3" maxWidth={960}>
      <XStack alignItems="center" justifyContent="space-between" gap="$3">
        <Text fontSize="$7" fontWeight="700">Archiv</Text>
        <Button
          theme="active"
          disabled={upload.isPending}
          onPress={() => fileInputRef.current?.click()}
          accessibilityLabel={upload.isPending ? 'Lädt hoch' : 'Hochladen'}
        >
          {upload.isPending ? (
            <XStack gap="$2" alignItems="center">
              <Spinner size="small" />
              <Text>Lädt hoch…</Text>
            </XStack>
          ) : (
            'Hochladen'
          )}
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf,image/*"
          style={{ display: 'none' }}
          onChange={(ev) => onFilePicked(ev as unknown as { target: HTMLInputElement })}
        />
      </XStack>

      {uploadMessage ? (
        <Paragraph color={upload.isError ? '$red10' : '$green10'}>{uploadMessage}</Paragraph>
      ) : null}

      <Input
        placeholder="Suche…"
        value={query}
        onChangeText={(t) => {
          setQuery(t);
          setSearchPartial({ query: t || undefined });
        }}
      />

      <YStack gap="$2">
        <FilterRow
          label="Tag"
          activeId={undefined}
          activeIds={tagIds}
          items={tagsList?.results ?? []}
          onToggle={toggleTag}
        />
        <FilterRow
          label="Korrespondent"
          activeId={correspondentId}
          items={correspondentsList?.results ?? []}
          onSelect={(id) =>
            setSearchPartial({ correspondent: id === correspondentId ? undefined : id })
          }
        />
        <FilterRow
          label="Dokumenttyp"
          activeId={documentTypeId}
          items={documentTypesList?.results ?? []}
          onSelect={(id) =>
            setSearchPartial({ documentType: id === documentTypeId ? undefined : id })
          }
        />
      </YStack>

      <Separator />

      {isLoading ? (
        <Spinner size="large" />
      ) : error ? (
        <Paragraph color="$red10">
          {error instanceof Error ? error.message : 'Archiv konnte nicht geladen werden.'}
        </Paragraph>
      ) : !data?.results.length ? (
        <Paragraph color="$color10">Keine Dokumente gefunden.</Paragraph>
      ) : (
        <YStack gap="$2">
          <Text fontSize="$2" color="$color10">{total} Treffer</Text>
          {data.results.map((doc) => (
            <DocumentRow
              key={doc.id}
              doc={doc}
              correspondents={correspondentsList?.results ?? []}
              onOpen={() => navigate({ to: '/archiv/$documentId', params: { documentId: String(doc.id) } })}
            />
          ))}
          <PaginationBar
            page={page}
            totalPages={totalPages}
            onChange={(p) => setSearchPartial({ page: p })}
          />
        </YStack>
      )}
    </YStack>
  );
}

function FilterRow<T extends { id: number; name: string }>({
  label,
  items,
  activeId,
  activeIds,
  onSelect,
  onToggle,
}: {
  label: string;
  items: T[];
  activeId?: number | undefined;
  activeIds?: number[];
  onSelect?: (id: number) => void;
  onToggle?: (id: number) => void;
}) {
  if (!items.length) return null;
  return (
    <XStack gap="$2" flexWrap="wrap" alignItems="center">
      <Text fontSize="$2" color="$color10" minWidth={110}>{label}:</Text>
      {items.map((item) => {
        const active = activeIds ? activeIds.includes(item.id) : activeId === item.id;
        return (
          <Button
            key={item.id}
            size="$2"
            theme={active ? 'active' : null}
            backgroundColor={active ? '$blue5' : '$color3'}
            borderColor={active ? '$blue8' : '$color7'}
            borderWidth={1}
            borderRadius="$10"
            onPress={() => (onToggle ? onToggle(item.id) : onSelect?.(item.id))}
          >
            {item.name}
          </Button>
        );
      })}
    </XStack>
  );
}

function DocumentRow({
  doc,
  correspondents,
  onOpen,
}: {
  doc: PaperlessDocument;
  correspondents: PaperlessCorrespondent[];
  onOpen: () => void;
}) {
  const corrName = doc.correspondent
    ? correspondents.find((c) => c.id === doc.correspondent)?.name ?? '—'
    : '—';
  return (
    <YStack
      pressStyle={{ backgroundColor: '$color3' }}
      hoverStyle={{ backgroundColor: '$color2' }}
      onPress={onOpen}
      padding="$3"
      borderRadius="$3"
      borderWidth={1}
      borderColor="$color5"
      cursor="pointer"
    >
      <Text fontSize="$4" fontWeight="600" numberOfLines={1}>{doc.title}</Text>
      <XStack gap="$3" alignItems="center" marginTop="$1">
        <Text fontSize="$2" color="$color10">{corrName}</Text>
        <Text fontSize="$2" color="$color10">·</Text>
        <Text fontSize="$2" color="$color10">{formatDate(doc.created)}</Text>
      </XStack>
    </YStack>
  );
}

function PaginationBar({
  page,
  totalPages,
  onChange,
}: {
  page: number;
  totalPages: number;
  onChange: (p: number) => void;
}) {
  if (totalPages <= 1) return null;
  return (
    <XStack gap="$2" alignItems="center" justifyContent="center" marginTop="$2">
      <Button size="$2" disabled={page <= 1} onPress={() => onChange(page - 1)}>←</Button>
      <Text fontSize="$3">{page} / {totalPages}</Text>
      <Button size="$2" disabled={page >= totalPages} onPress={() => onChange(page + 1)}>→</Button>
    </XStack>
  );
}

function parseCsvInts(value: string | undefined): number[] {
  if (!value) return [];
  return value
    .split(',')
    .map((s) => Number(s.trim()))
    .filter((n) => Number.isFinite(n));
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('de-DE', { dateStyle: 'medium' });
}

export interface ArchivSearch {
  query?: string;
  tags?: string;
  correspondent?: number;
  documentType?: number;
  page?: number;
}
